import { RequestOptions } from '../utils/types';
import { replaceURLParameters } from './replaceURLParameters';

/**
 * ABS_URL_REGEX
 *
 * Used to check if a URL is absolute or relative
 * Checks for <SOME_TEXT>://, eg. https:// or file://
 */
const ABS_URL_REGEX = /^(?:[a-z]+:)?\/\//i;

/**
 * BASE_URL
 *
 * The Easel TV Client API URL - to be extended with specific endpoints
 *
 * @TODO: Will later be replaced by a generic api base and we will send tenant headers
 * eg. https://api.suggestedtv.com/client/v1
 */
export const BASE_URL = `https://etv-dev.suggestedtv.com/api/client/v1`;

/**
 * formAbsoluteUrl
 *
 * Form an absolute URL from the BASE_URL if a relative endpoint is given
 * Or returns the given absolute URL
 *
 * @param endpoint An absolute or relative URL
 * @returns An absolute URL
 */
export const formAbsoluteUrl = (endpoint: string, baseUrl: string): URL => {
    if (ABS_URL_REGEX.test(endpoint)) {
        return new URL(endpoint);
    }
    if (endpoint.charAt(0) === '/') {
        return new URL(`${baseUrl}${endpoint}`);
    }
    return new URL(`${baseUrl}/${endpoint}`);
};

/**
 * makeUrl
 *
 * Constructs a full absolute URL given an endpoint and query/parameter options
 *
 * @param endpoint - The location to send the request
 * @param options - An object containing any queries to append or parameters to interpolate
 */
export const makeUrl = (
    endpoint: string,
    baseUrl = BASE_URL,
    options: RequestOptions = {},
): string => {
    const { params, queries } = options;

    // Replace parameters in the URL eg. :id, with values provided in the options
    const parameterizedEndpoint = replaceURLParameters(endpoint, params);

    // Checks whether the endpoint provided is a relative path or absolute URL
    const url = formAbsoluteUrl(parameterizedEndpoint, baseUrl);

    // Removes any queries with empty string values
    const sanitizedQueries =
        queries &&
        Object.entries(queries).reduce(
            (obj, [key, value]) =>
                value.length
                    ? {
                          ...obj,
                          [key]: value,
                      }
                    : obj,
            {},
        );

    // Get search parameter string
    const searchParams = new URLSearchParams(sanitizedQueries).toString();

    // Return constructed URL
    if (searchParams) {
        return `${url.toString()}?${searchParams}`;
    }

    return url.toString();
};
